import React, { useMemo, useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useTenant } from './TenantContext';
import {
  DEFAULT_BRAND_COLOR,
  deriveBrandHover,
  deriveBrandInk,
  getContrastText,
  hexToHslTriplet,
  isValidHex
} from '../utils/colorUtils';

// Light-mode fallback, used when the tenant has no branding stored yet.
// Same canonical default the server sends for an unbranded tenant.
const defaultThemeConfig = {
  primaryColor: DEFAULT_BRAND_COLOR,
};

// Dark mode is a controlled, platform-owned palette — tenants cannot brand it.
// These constants are the single source of truth for dark accents.
const darkBrand = {
  primary: '#3CAEA3', // Thaklis teal
  secondary: '#5794F2', // Grafana blue
};

// Grafana status colors
const grafanaColors = {
  info: '#5794F2',     // Grafana blue
  success: '#73BF69',  // Grafana green
  warning: '#FF9830',  // Grafana orange
  error: '#F2495C',    // Grafana red
  purple: '#B877D9',   // Grafana purple
};

// Dark mode colors (Grafana-inspired)
const darkPalette = {
  background: {
    default: '#111217',
    paper: '#181b1f',
  },
  text: {
    primary: '#e9edf2',
    secondary: '#9da5b0',
    disabled: '#6b7280',
  },
};

const lightPalette = {
  background: {
    default: '#f5f5f5',
    paper: '#ffffff',
  },
  text: {
    primary: '#344767',
    secondary: '#7b809a',
  },
};

/**
 * Resolve the light-theme brand pair from stored tenant config.
 *
 * `primaryColor` is the Brand Color and the sole source of tenant identity.
 * The secondary slot carries BRAND INK — the brand darkened only as far as it
 * needs to be legible as foreground on white. It is always derived here and
 * never read from the stored `secondaryColor`: legacy rows hold a
 * fixed-luminance companion that would wash the chosen colour out. The stored
 * field stays in the API/DB contract and is refreshed to brand ink on save.
 */
const resolveBrandColors = (tenantTheme) => {
  const primaryColor = isValidHex(tenantTheme?.primaryColor)
    ? tenantTheme.primaryColor
    : defaultThemeConfig.primaryColor;
  return { primaryColor, secondaryColor: deriveBrandInk(primaryColor) };
};

const TenantThemeProvider = ({ children }) => {
  const { theme: tenantTheme, loading } = useTenant();
  const [isDarkMode, setIsDarkMode] = useState(() =>
    document.documentElement.classList.contains('dark')
  );

  // Watch for dark class changes on document root
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const theme = useMemo(() => {
    const { primaryColor, secondaryColor } = resolveBrandColors(tenantTheme);
    // BRAND — the exact tenant colour in light mode, a fixed accent in dark.
    const effectivePrimary = isDarkMode ? darkBrand.primary : primaryColor;
    // BRAND INK — brand-coloured foreground on white. Drives every surface
    // where the brand sits ON paper rather than under it: tab indicators,
    // focus rings, outlined controls.
    const effectiveSecondary = isDarkMode ? darkBrand.secondary : secondaryColor;
    const palette = isDarkMode ? darkPalette : lightPalette;

    return createTheme({
      palette: {
        mode: isDarkMode ? 'dark' : 'light',
        // Tenant-branded slots: contrastText is derived so a light brand colour
        // still gets a readable label. Semantic colours below stay fixed.
        primary: {
          main: effectivePrimary,
          // `dark` is what MUI reaches for on contained-button hover. Pinning it
          // to BRAND HOVER keeps that interaction a modest step rather than
          // MUI's heavier default darkening.
          dark: deriveBrandHover(effectivePrimary),
          contrastText: getContrastText(effectivePrimary),
        },
        secondary: {
          main: effectiveSecondary,
          contrastText: getContrastText(effectiveSecondary),
        },
        // Grafana status colors
        info: {
          main: grafanaColors.info,
          contrastText: '#ffffff',
        },
        success: {
          main: grafanaColors.success,
          contrastText: '#ffffff',
        },
        warning: {
          main: grafanaColors.warning,
          contrastText: '#000000',
        },
        error: {
          main: grafanaColors.error,
          contrastText: '#ffffff',
        },
        background: palette.background,
        text: palette.text,
        divider: isDarkMode ? '#2c3039' : 'rgba(0,0,0,0.12)',
      },
      typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: { fontWeight: 600 },
        h2: { fontWeight: 600 },
        h3: { fontWeight: 600 },
        h4: { fontWeight: 600 },
        h5: { fontWeight: 600 },
        h6: { fontWeight: 600 },
      },
      shape: {
        borderRadius: 8,
      },
      components: {
        MuiButton: {
          // A normal action is a brand moment: default contained/text buttons
          // carry the exact Brand Color. Semantic buttons pass an explicit
          // color prop, which always beats defaultProps, so error/success/
          // warning/info actions are untouched by this.
          defaultProps: {
            color: 'primary',
          },
          styleOverrides: {
            root: {
              textTransform: 'none',
              fontWeight: 500,
            },
          },
        },
        MuiTabs: {
          // The indicator and the selected label sit on white, so they use
          // BRAND INK rather than raw brand — a pale brand would otherwise
          // leave an invisible indicator.
          defaultProps: {
            indicatorColor: 'primary',
            textColor: 'primary',
          },
          styleOverrides: {
            indicator: {
              backgroundColor: effectiveSecondary,
            },
            // Scoped through the Tabs root so it outranks MUI's own
            // .MuiTab-textColorPrimary.Mui-selected rule rather than tying with
            // it and depending on stylesheet injection order.
            root: {
              '& .MuiTab-root.Mui-selected': {
                color: effectiveSecondary,
              },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: palette.background.paper,
              boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
              borderColor: isDarkMode ? '#2c3039' : undefined,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundColor: palette.background.paper,
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: isDarkMode ? '#181b1f' : undefined,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            },
          },
        },
        // One table standard for the whole app (matches ProTable / Work Orders): 12px semibold headers,
        // 13px cells, compact rows. Pages that set their own cell sx still win; :where() keeps the
        // Typography rule at zero specificity so an explicit sx fontSize on text inside a cell is kept.
        MuiTableCell: {
          styleOverrides: {
            root: {
              borderColor: isDarkMode ? '#2c3039' : 'rgba(224, 224, 224, 1)',
              fontSize: '0.8125rem',
              lineHeight: 1.4,
              '& :where(.MuiTypography-body1, .MuiTypography-body2)': { fontSize: '0.8125rem' },
            },
            head: {
              fontSize: '0.75rem',
              fontWeight: 600,
            },
            sizeSmall: {
              paddingTop: 6,
              paddingBottom: 6,
            },
            sizeMedium: {
              paddingTop: 10,
              paddingBottom: 10,
            },
          },
        },
        MuiTableHead: {
          styleOverrides: {
            root: {
              backgroundColor: isDarkMode ? '#22252b' : '#f5f5f5',
              '& .MuiTableCell-head': {
                color: isDarkMode ? '#e9edf2' : undefined,
              },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              backgroundColor: isDarkMode ? '#22252b' : undefined,
            },
          },
        },
        MuiDivider: {
          styleOverrides: {
            root: {
              borderColor: isDarkMode ? '#2c3039' : 'rgba(0, 0, 0, 0.12)',
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              backgroundColor: isDarkMode ? '#22252b' : undefined,
            },
          },
        },
        MuiOutlinedInput: {
          // Focus ring sits on paper — BRAND INK, so it stays visible even
          // when the Brand Color itself is pale.
          styleOverrides: {
            root: {
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: effectiveSecondary,
              },
            },
            notchedOutline: {
              borderColor: isDarkMode ? '#3d4249' : 'rgba(0, 0, 0, 0.23)',
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              '&.Mui-focused': {
                color: effectiveSecondary,
              },
            },
          },
        },
      },
    });
  }, [tenantTheme, isDarkMode]);

  // Apply CSS custom properties for tenant colors (both legacy and shadcn)
  React.useEffect(() => {
    const root = document.documentElement;
    const brand = resolveBrandColors(tenantTheme);
    const primaryColor = isDarkMode ? darkBrand.primary : brand.primaryColor;
    const secondaryColor = isDarkMode ? darkBrand.secondary : brand.secondaryColor;

    // Legacy CSS variables
    root.style.setProperty('--tenant-primary', primaryColor);
    root.style.setProperty('--tenant-secondary', secondaryColor);

    // --accent is deliberately NOT tenant-driven. In shadcn it is the neutral
    // hover/focus slot (ghost + outline buttons, dropdown and select rows) and
    // is always paired with --accent-foreground, which index.css keeps as a
    // dark neutral. Painting a dark brand colour into --accent while its
    // foreground stayed dark is what made those states unreadable.
    root.style.removeProperty('--accent');

    // shadcn/ui CSS variables (HSL format).
    // Dark mode is controlled by the `.dark` block in index.css — clear the
    // inline overrides so the stylesheet wins instead of a stale light value.
    if (isDarkMode) {
      ['--primary', '--primary-foreground', '--ring'].forEach((v) => root.style.removeProperty(v));
      return;
    }

    // shadcn --primary and --ring back filled buttons and focus rings that sit
    // on paper, so they take BRAND INK rather than the raw Brand Color — a
    // pale brand would otherwise give an invisible ring. --primary-foreground
    // is derived alongside --primary; leaving them out of step is what made
    // these states unreadable before.
    const brandInk = secondaryColor;
    root.style.setProperty('--primary', hexToHslTriplet(brandInk));
    root.style.setProperty('--primary-foreground', hexToHslTriplet(getContrastText(brandInk)));
    root.style.setProperty('--ring', hexToHslTriplet(brandInk));
  }, [tenantTheme, isDarkMode]);

  if (loading) {
    // Show a minimal loading state while tenant config loads
    return (
      <ThemeProvider theme={createTheme()}>
        <CssBaseline />
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#f5f5f5'
        }}>
          <div style={{
            width: 40,
            height: 40,
            border: '3px solid #e0e0e0',
            borderTopColor: '#3CAEA3',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

export default TenantThemeProvider;
